import React, { CSSProperties, useCallback } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { TooltipProps } from '@mui/material/Tooltip/Tooltip';
import useIsMobile from 'hooks/useIsMobile';
import { showToast } from 'services/toaster';
import { TOASTER_TYPES } from 'components/core/Toaster/types';

interface ForbiddenTooltipProps {
  visibleWhen: boolean;
  children: React.ReactElement;
  title?: string;
  reason?: string;
  reasons?: string[] | null;
  inlined?: boolean;
  style?: CSSProperties;
  placement?: TooltipProps['placement'];
  values?: any;
}

const wrapperBaseStyle: CSSProperties = {
  maxWidth: '100%',
  pointerEvents: 'auto',
};

/** Inline wrappers (toolbar buttons): span + inline-flex keeps hover on disabled children. */
const inlineWrapperStyle: CSSProperties = {
  ...wrapperBaseStyle,
  display: 'inline-flex',
  width: 'fit-content',
  verticalAlign: 'middle',
};

/** Block wrappers (forms, boxes, full-width controls): div preserves block flow. */
const blockWrapperStyle: CSSProperties = {
  ...wrapperBaseStyle,
  display: 'block',
  width: '100%',
};

const ForbiddenTooltip = ({
  visibleWhen,
  children,
  title,
  style,
  reason = 'tooltip_action_forbidden',
  reasons,
  inlined,
  placement,
  values,
}: ForbiddenTooltipProps): JSX.Element => {
  const intl = useIntl();
  const isMobile = useIsMobile();

  const reasonsGrouped: string =
    reasons && reasons.length > 0
      ? reasons
          .map((reason) => formatTranslation(intl, unsafeTranslationKey(reason) , values))
          .join(', ')
      : (reason && formatTranslation(intl, unsafeTranslationKey(reason) , values)) || '';

  const translatedMessageToDisplay =
    title ??
    reasonsGrouped
      .replaceAll('%d', values?.['%d'])
      .replaceAll('%s', values?.['%s']);

  const onClick = useCallback(() => {
    if (!isMobile) {
      return;
    }
    showToast({
        message: translatedMessageToDisplay,
        type: TOASTER_TYPES.SUCCESS,
      });
  }, [isMobile, translatedMessageToDisplay]);

  if (!visibleWhen) {
    return children;
  }

  const Wrapper = inlined ? 'span' : 'div';

  return (
    <Tooltip
      arrow
      enterTouchDelay={0}
      title={translatedMessageToDisplay}
      placement={placement ?? 'top'}
      disableTouchListener
      PopperProps={{
        popperOptions: {
          strategy: 'fixed',
        },
      }}
    >
      <Wrapper
        onClick={onClick}
        style={{
          ...(inlined ? inlineWrapperStyle : blockWrapperStyle),
          cursor: 'not-allowed',
          ...style,
        }}
      >
        {children}
      </Wrapper>
    </Tooltip>
  );
};

export const MemoForbiddenTooltip = React.memo(ForbiddenTooltip);
export default ForbiddenTooltip;
